import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import "./datatable.scss";
import axios from "../../../../../api/axios";
import { recipeMenuColumns } from "../../datatablesource";

import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";

const RecipesAndMenuDatatable = () => {
  const [recipesData, setRecipesData] = useState([]);
  const [allMenuItemsData, setAllMenuItemsData] = useState([]);
  const [menuItemsData, setMenuItemsData] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState("");
  const [containers, setContainers] = useState([]);
  const [editRecipeId, setEditRecipeId] = useState(null);

  const [linkedMenuItems, setLinkedMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [menuSearchText, setMenuSearchText] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);

    const restaurantId = sessionStorage.getItem("configBizEntityId");

    try {
      const recipesResponse = await axios.get(
        `businessEntities/getAllRecipesAndMenuItemsViaRestoId/${restaurantId}`,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        }
      );

      const recipes = recipesResponse.data.recipes || [];

      setRecipesData(recipes);

      const allLinkedMenuItems = recipes.flatMap((recipe) =>
        (recipe.menuItems || []).map((item) => item.menuItem)
      );

      setLinkedMenuItems(allLinkedMenuItems);

      const menuItemsResponse = await axios.get(
        `businessEntities/getAllMenuItems/${restaurantId}`,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        }
      );

      const menus = menuItemsResponse.data.allMenus || [];

      setAllMenuItemsData(menus);
      setMenuItemsData(menus);
    } catch (error) {
      console.error("Error fetching data:", error);
      setSnackbarMessage("Error fetching data");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (recipeId) => {
    const recipe = recipesData.find((item) => item.recipeId === recipeId);

    if (!recipe) return;

    const currentLinkedMenuItems = recipe.menuItems || [];

    const selectedContainers = currentLinkedMenuItems.map((item) => ({
      recipe: recipe.recipeName,
      menuItem: item.menuItem,
      menuId: item.menuItemId,
    }));

    const currentRecipeLinkedMenuItemNames = currentLinkedMenuItems.map(
      (item) => item.menuItem
    );

    const availableMenuItems = allMenuItemsData.filter((menuItem) => {
      const isLinkedToAnyRecipe = linkedMenuItems.includes(menuItem.menu_name);

      const isLinkedToCurrentRecipe = currentRecipeLinkedMenuItemNames.includes(
        menuItem.menu_name
      );

      return !isLinkedToAnyRecipe || isLinkedToCurrentRecipe;
    });

    setSelectedRecipe(recipe.recipeName);
    setEditRecipeId(recipeId);
    setContainers(selectedContainers);
    setMenuItemsData(availableMenuItems);
    setMenuSearchText("");
    setOpenModal(true);
  };

  const handleAutocompleteChange = (event, newValue) => {
    const updatedContainers = newValue.map((item) => ({
      recipe: selectedRecipe,
      menuItem: item.menu_name,
      menuId: item.menu_id,
    }));

    setContainers(updatedContainers);
  };

  const handleDeleteSelectedMenuItem = (menuId) => {
    const updatedContainers = containers.filter(
      (container) => container.menuId !== menuId
    );

    setContainers(updatedContainers);
  };

  const handleClearAllLinks = () => {
    setContainers([]);
    setMenuSearchText("");
  };

  const handleUpdate = async () => {
    try {
      const restaurantId = sessionStorage.getItem("configBizEntityId");

      const menuItems = containers.map((container) => ({
        menu_id: container.menuId,
        menuItem: container.menuItem,
      }));

      await axios.post(`businessEntities/updateRecipeMenuLinks`, {
        recipeId: editRecipeId,
        restaurantId,
        menuItems,
      });

      setSnackbarMessage("Updated successfully!");
      setSnackbarOpen(true);
      setOpenModal(false);

      resetModalState();
      fetchData();
    } catch (error) {
      console.error("Error updating recipe-menu links:", error);
      setSnackbarMessage("Error Linking Menu To Recipe");
      setSnackbarOpen(true);
    }
  };

  const resetModalState = () => {
    setSelectedRecipe("");
    setEditRecipeId(null);
    setContainers([]);
    setMenuItemsData(allMenuItemsData);
    setMenuSearchText("");
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    resetModalState();
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      width: 350,
      renderCell: (params) => (
        <div className="cellAction">
          <div
            className="editButton"
            onClick={() => handleEditClick(params.row.recipeId)}
          >
            Edit / View
          </div>
        </div>
      ),
    },
  ];

  const selectedAutocompleteValue = containers.map((container) => ({
    menu_id: container.menuId,
    menu_name: container.menuItem,
  }));

  return (
    <div className="datatable">
      <div
        className="datatableTitle"
        style={{ display: "flex", alignItems: "center" }}
      >
        Link Recipes To Menu Items
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <DataGrid
          className="datagrid"
          rows={recipesData.map((recipe) => ({
            ...recipe,
            menuItems: recipe.menuItems || [],
          }))}
          getRowId={(row) => row.recipeId}
          columns={recipeMenuColumns.concat(actionColumn)}
          pageSize={9}
          rowsPerPageOptions={[9]}
        />
      )}

      <Modal
        open={openModal}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }

          handleCloseModal();
        }}
        aria-labelledby="link-menu-items-modal"
        aria-describedby="link-menu-items-description"
      >
        <div
          className="modal-container"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            padding: "24px",
            width: "90%",
            maxWidth: "680px",
            maxHeight: "85vh",
            overflowY: "auto",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
            outline: "none",
          }}
        >
          <h3
            id="link-menu-items-modal"
            style={{
              marginTop: 0,
              marginBottom: "6px",
              color: "#222",
            }}
          >
            Link Menu Items
          </h3>

          <p
            id="link-menu-items-description"
            style={{
              marginTop: 0,
              marginBottom: "24px",
              color: "#666",
              fontSize: "14px",
            }}
          >
            Search and select menu items that should be linked with this recipe.
          </p>

          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: 600,
                marginBottom: "6px",
                color: "#333",
              }}
            >
              Recipe
            </label>

            <div
              style={{
                padding: "12px 14px",
                borderRadius: "8px",
                backgroundColor: "#f7f7f7",
                border: "1px solid #ddd",
                fontWeight: 500,
                color: "#222",
              }}
            >
              {selectedRecipe || "No recipe selected"}
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disableClearable
              options={menuItemsData}
              value={selectedAutocompleteValue}
              inputValue={menuSearchText}
              onInputChange={(event, newInputValue, reason) => {
                if (reason !== "reset") {
                  setMenuSearchText(newInputValue);
                }
              }}
              getOptionLabel={(option) => option?.menu_name || ""}
              isOptionEqualToValue={(option, value) =>
                option?.menu_id === value?.menu_id
              }
              onChange={handleAutocompleteChange}
              renderTags={() => null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Menu Items"
                  placeholder="Search and select menu items..."
                  fullWidth
                />
              )}
              noOptionsText="No menu items found"
            />
          </div>

          <div
            style={{
              marginBottom: "24px",
              padding: "14px",
              border: "1px solid #e5e5e5",
              borderRadius: "10px",
              backgroundColor: "#fafafa",
              minHeight: "78px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#333",
                }}
              >
                Selected Menu Items
              </div>

              {containers.length > 0 && (
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  onClick={handleClearAllLinks}
                  style={{
                    textTransform: "none",
                    fontSize: "13px",
                    padding: "2px 6px",
                  }}
                >
                  Clear All Links
                </Button>
              )}
            </div>

            {containers.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                {containers.map((container) => (
                  <Chip
                    key={container.menuId}
                    label={container.menuItem}
                    onDelete={() =>
                      handleDeleteSelectedMenuItem(container.menuId)
                    }
                    variant="outlined"
                    sx={{
                      borderRadius: "8px",
                      fontSize: "14px",
                      backgroundColor: "#fff",
                      "& .MuiChip-deleteIcon": {
                        fontSize: "18px",
                      },
                    }}
                  />
                ))}
              </div>
            ) : (
              <div
                style={{
                  fontSize: "13px",
                  color: "#888",
                }}
              >
                No menu items selected.
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
            }}
          >
            <Button variant="outlined" onClick={handleCloseModal}>
              Close
            </Button>

            <Button
              variant="contained"
              onClick={handleUpdate}
              disabled={!editRecipeId}
            >
              Update
            </Button>
          </div>
        </div>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        autoHideDuration={6000}
      />
    </div>
  );
};

export default RecipesAndMenuDatatable;